import { useState } from 'react';
import { FiMessageSquare, FiX } from 'react-icons/fi';
import { QueryClientProvider } from '@tanstack/react-query';

import { cn } from '@/shared/lib';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/ui/sheet';
import { topBarIconButtonClasses, topBarIconClasses } from '@/shared/layout/top-bar/styles';
import { assistantQueryClient } from '@/modules/assistant/query-client';
import { assistantScopeForRoute } from '@/modules/assistant/services/assistant-scope';
import { useAssistantContext, useAssistantRoute } from '@/modules/assistant/hooks';
import { AssistantPanelContent } from '@/modules/assistant/parts/panel-content';

/**
 * The assistant panel, mounted in the app shell.
 *
 * ---------------------------------------------------------------------------
 * WHY A DRAWER AND NOT A COLUMN
 * ---------------------------------------------------------------------------
 * The panel has to be reachable from every page without taking any of them over.
 * A drawer does that without a layout decision per page: the page behind it keeps
 * its own width, and the panel closes when the person is done with it.
 *
 * The drawer is a real dialog — it traps focus while it is open, it takes Escape,
 * and it returns focus to the control that opened it — so the panel is reachable
 * and operable from the keyboard without a single bespoke key handler.
 *
 * ---------------------------------------------------------------------------
 * WHY THE CONTEXT IS READ ONLY WHEN THE PANEL IS OPEN
 * ---------------------------------------------------------------------------
 * The context is one request per route, and a page the person never asks anything
 * on has no use for it. So the read waits for the panel to open.
 */
export function AssistantPanel() {
  return (
    <QueryClientProvider client={assistantQueryClient}>
      <AssistantPanelSheet />
    </QueryClientProvider>
  );
}

/** The drawer, and the control that opens it. */
function AssistantPanelSheet() {
  const [open, setOpen] = useState(false);
  const route = useAssistantRoute();
  const scope = assistantScopeForRoute(route);
  const context = useAssistantContext(scope, route, { enabled: open });

  return (
    <Sheet open={open} onOpenChange={setOpen} side="right">
      <SheetTrigger
        className={cn(topBarIconButtonClasses, 'p-0')}
        aria-label="Open the Lakshi assistant"
      >
        <FiMessageSquare className={topBarIconClasses} aria-hidden="true" />
      </SheetTrigger>

      <SheetContent>
        <SheetHeader className="flex-row items-start justify-between gap-3">
          <div className="flex flex-col gap-1.5">
            <SheetTitle>{context.data?.agent.name ?? 'Assistant'}</SheetTitle>
            <SheetDescription>
              {context.data?.agent.tagline ?? 'Ask about this page.'}
            </SheetDescription>
          </div>
          <SheetClose className="h-8 w-8 shrink-0 p-0" aria-label="Close the assistant">
            <FiX className={topBarIconClasses} aria-hidden="true" />
          </SheetClose>
        </SheetHeader>

        {/*
          `min-h-0` is load-bearing: without it this body refuses to shrink below
          its content, the drawer grows past the viewport, and the whole drawer
          scrolls instead of the conversation inside it.
        */}
        <SheetBody className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          <AssistantPanelContent route={route} scope={scope} />
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
